import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import "./DailyTargetWidget.css";

const COMMITMENT_MAP = {
  "30min": { label: "30 min", minutes: 30 },
  "1hr": { label: "1 hour", minutes: 60 },
  "2hr": { label: "2 hours", minutes: 120 },
  "2hr+": { label: "2+ hours", minutes: 150 },
};

// hitHistory: bool[] of last 14 days — pass from parent
export default function DailyTargetWidget({ todayMinutes = 0, hitHistory = [] }) {
  const { user } = useContext(AuthContext);

  const commitment = user?.onboarding?.dailyCommitment;
  const target = COMMITMENT_MAP[commitment] || { label: "30 min", minutes: 30 };
  const pct = Math.min(Math.round((todayMinutes / target.minutes) * 100), 100);
  const remaining = Math.max(0, target.minutes - todayMinutes);
  const isComplete = pct >= 100;

  const recentHits = hitHistory.slice(-14);
  const hitCount = recentHits.filter(Boolean).length;
  const hitRate = recentHits.length > 0 ? Math.round((hitCount / recentHits.length) * 100) : 0;

  const circumference = 2 * Math.PI * 46;
  const offset = circumference * (1 - pct / 100);

  const getMessage = () => {
    if (isComplete) return "Daily goal hit. Your streak and weekly pace both just got stronger.";
    if (pct >= 75) return `${remaining}m left. You're almost there — push through.`;
    if (pct >= 40) return `${remaining}m to your goal. A focused session closes it.`;
    return `${remaining}m to go. Starting is the hardest part.`;
  };

  return (
    <div className={`dtw-card ${isComplete ? "dtw-card--complete" : ""}`}>
      <div className="dtw-header">
        <h3 className="dtw-title">Today's target</h3>
        <span className="dtw-target-label">{target.label}</span>
      </div>

      <div className="dtw-body">
        <div className="dtw-ring-area">
          <svg viewBox="0 0 110 110" className="dtw-ring">
            <circle cx="55" cy="55" r="46" className="dtw-ring-bg" />
            <circle
              cx="55" cy="55" r="46"
              className={`dtw-ring-fill ${isComplete ? "dtw-ring-fill--complete" : ""}`}
              style={{
                strokeDasharray: circumference,
                strokeDashoffset: offset,
              }}
            />
          </svg>
          <div className="dtw-ring-inner">
            <span className="dtw-ring-pct">{pct}%</span>
            <span className="dtw-ring-time">{todayMinutes}m</span>
          </div>
        </div>

        <div className="dtw-right">
          <p className="dtw-message">{getMessage()}</p>

          {/* 14-day hit rate */}
          {recentHits.length > 0 && (
            <div className="dtw-history">
              <div className="dtw-history-dots">
                {recentHits.map((hit, i) => (
                  <div
                    key={i}
                    className={`dtw-dot ${hit ? "dtw-dot--hit" : ""} ${i === recentHits.length - 1 ? "dtw-dot--today" : ""}`}
                    title={hit ? "Goal hit" : "Missed"}
                  />
                ))}
              </div>
              <div className="dtw-history-label">
                {hitCount}/{recentHits.length} days hit ({hitRate}%) — last 14 days
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}